import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { Button } from '@shared/components/ui/button';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportHistoryItem } from '../../models/ImportHistoryModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faClock,
  faExclamationTriangle,
  faTruck,
  faCreditCard,
  faInfoCircle,
  faRedo,
} from '@fortawesome/pro-light-svg-icons';
import { cn } from '@shared/utils/utils';

export interface ImportHistoryDetailModalRef {
  open: (item: ImportHistoryItem) => void;
  close: () => void;
}

export interface ImportHistoryDetailModalProps {
  onRetry?: (item: ImportHistoryItem) => void;
}

export const ImportHistoryDetailModal = forwardRef<ImportHistoryDetailModalRef, ImportHistoryDetailModalProps>(
  ({ onRetry }, ref) => {
    const { t } = useTranslation('shopsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const [item, setItem] = useState<ImportHistoryItem | null>(null);
    const [activeTab, setActiveTab] = useState<string>('data');

    useImperativeHandle(ref, () => ({
      open: (newItem) => {
        // Set initial data if available from list
        setItem(newItem);
        setActiveTab('data');

        // Open modal immediately
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
        setItem(null);
      },
    }));

    return (
      <ModalCenter
        ref={modalRef}
        title={item ? `${t('importHistory.detail.title', 'Detalle de Importación')} ${item.id}` : ''}
        width="xl"
        type="default"
        classNameBody="max-h-[85vh] overflow-y-auto"
      >
        {item ? (
          <div className="flex flex-col gap-6 p-1">
            {/* Header con resumen rápido */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-body-bg/40 p-4 rounded-xl border border-card-border">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-text-secondary uppercase tracking-widest font-bold">
                  {t('importHistory.table.status', 'Estado')}
                </span>
                <Badge
                  className={cn(
                    'text-xs font-medium border-0 shadow-none w-fit',
                    item.status === 'imported'
                      ? 'bg-bg-success text-success dark:text-bright-success'
                      : item.status === 'failed'
                        ? 'bg-bg-error text-error'
                        : item.status === 'duplicated'
                          ? 'bg-bg-warning text-warning'
                          : 'bg-body-bg text-text-secondary border border-card-border/50',
                  )}
                >
                  {t(`importHistory.status.${item.status}`, item.status)}
                </Badge>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-text-secondary uppercase tracking-widest font-bold">
                  {t('importHistory.table.externalRef', 'Referencia Externa')}
                </span>
                <span className="text-sm font-medium">{item.external_order_ref?.replace(/^#/, '')}</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-text-secondary uppercase tracking-widest font-bold">
                  <FontAwesomeIcon icon={faClock} className="mr-1" />
                  {t('importHistory.table.processingTimeMs', 'Procesamiento')}
                </span>
                <span className="text-sm font-medium">{item.processing_time_ms}ms</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-text-secondary uppercase tracking-widest font-bold">
                  {t('importHistory.table.importedAt', 'Fecha')}
                </span>
                <span className="text-sm font-medium">{new Date(item.imported_at).toLocaleString()}</span>
              </div>

              {item.status === 'failed' && onRetry && (
                <div className="flex items-center justify-end col-span-2 md:col-span-4 border-t border-card-border pt-4 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRetry(item)}
                    className="gap-2 text-primary hover:text-bright-primary border-primary/20 hover:border-primary"
                  >
                    <FontAwesomeIcon icon={faRedo} className="h-3.5 w-3.5" />
                    {t('importHistory.actions.retry', 'Reintentar')}
                  </Button>
                </div>
              )}
            </div>

            {/* Mensaje de error si existe */}
            {item.error_message && (
              <div className="flex items-start gap-3 bg-error/5 border border-error/20 p-4 rounded-xl text-error">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {t('importHistory.detail.errorMessage', 'Error detectado')}
                  </span>
                  <p className="text-sm opacity-90">{item.error_message}</p>
                </div>
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="data">{t('importHistory.detail.tabs.data', 'Datos del Pedido')}</TabsTrigger>
                <TabsTrigger value="filters">{t('importHistory.detail.tabs.filters', 'Filtros Aplicados')}</TabsTrigger>
              </TabsList>

              <TabsContent value="data" className="mt-0">
                <div className="flex flex-col gap-4">
                  {(() => {
                    const hasShipping = !!(
                      item.order_data_snapshot?.shipping_name ||
                      item.order_data_snapshot?.email ||
                      item.order_data_snapshot?.phone ||
                      item.order_data_snapshot?.shipping_address1
                    );

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Order Info (Left) */}
                        <div className="bg-body-bg/20 border border-card-border rounded-xl p-4 flex flex-col gap-4">
                          <h4 className="text-xs font-bold text-text-secondary uppercase tracking-widest flex items-center gap-2 mb-2">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            {t('importHistory.detail.sections.order', 'Información del Pedido')}
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            {item.order_data_snapshot?.ref && (
                              <div className="flex flex-col">
                                <span className="text-[10px] text-text-secondary uppercase font-bold">
                                  {t('importHistory.detail.fields.ref', 'Referencia')}
                                </span>
                                <span className="text-sm font-medium">
                                  {item.order_data_snapshot.ref?.replace(/^#/, '')}
                                </span>
                              </div>
                            )}
                            {item.order_data_snapshot?.external_id && (
                              <div className="flex flex-col">
                                <span className="text-[10px] text-text-secondary uppercase font-bold">
                                  {t('importHistory.detail.fields.externalId', 'ID Externo')}
                                </span>
                                <span
                                  className="text-sm font-medium truncate"
                                  title={item.order_data_snapshot.external_id}
                                >
                                  {item.order_data_snapshot.external_id}
                                </span>
                              </div>
                            )}
                            {item.order_data_snapshot?.currency && (
                              <div className="flex flex-col">
                                <span className="text-[10px] text-text-secondary uppercase font-bold">
                                  {t('importHistory.detail.fields.currency', 'Moneda')}
                                </span>
                                <span className="text-sm font-medium">{item.order_data_snapshot.currency}</span>
                              </div>
                            )}
                            {item.order_data_snapshot?.order_created_at && (
                              <div className="flex flex-col">
                                <span className="text-[10px] text-text-secondary uppercase font-bold">
                                  {t('importHistory.detail.fields.createdAt', 'Fecha Origen')}
                                </span>
                                <span className="text-sm font-medium truncate">
                                  {new Date(item.order_data_snapshot.order_created_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment & Totals (Right) */}
                        <div className="bg-body-bg/20 border border-card-border rounded-xl p-4 flex flex-col gap-4">
                          <h4 className="text-xs font-bold text-text-secondary uppercase tracking-widest flex items-center gap-2 mb-2">
                            <FontAwesomeIcon icon={faCreditCard} />
                            {t('importHistory.detail.sections.payment', 'Pago y Totales')}
                          </h4>
                          <div className="space-y-3">
                            {item.order_data_snapshot?.external_payment_method && (
                              <div className="flex flex-col">
                                <span className="text-[10px] text-text-secondary uppercase font-bold">
                                  {t('importHistory.detail.fields.paymentMethod', 'Método de Pago')}
                                </span>
                                <span className="text-sm font-medium">
                                  {item.order_data_snapshot.external_payment_method}
                                </span>
                                {item.order_data_snapshot?.external_financial_status && (
                                  <Badge
                                    variant="outline"
                                    className="w-fit mt-1 text-[10px] h-5 py-0 border-card-border bg-card-bg/50"
                                  >
                                    {item.order_data_snapshot.external_financial_status}
                                  </Badge>
                                )}
                              </div>
                            )}
                            <div className="grid grid-cols-1 gap-2 text-xs">
                              {(item.order_data_snapshot?.total_shipping ?? 0) > 0 && (
                                <div className="flex justify-between border-b border-card-border pb-1">
                                  <span className="text-text-secondary">
                                    {t('importHistory.detail.fields.shipping', 'Envío')}
                                  </span>
                                  <span className="font-medium text-primary">
                                    +{item.order_data_snapshot?.total_shipping} {item.order_data_snapshot?.currency}
                                  </span>
                                </div>
                              )}
                              {(item.order_data_snapshot?.total_tax ?? 0) > 0 && (
                                <div className="flex justify-between border-b border-card-border pb-1">
                                  <span className="text-text-secondary">
                                    {t('importHistory.detail.fields.tax', 'Impuestos')}
                                  </span>
                                  <span className="font-medium">
                                    +{item.order_data_snapshot?.total_tax} {item.order_data_snapshot?.currency}
                                  </span>
                                </div>
                              )}
                              {(item.order_data_snapshot?.total_discount ?? 0) > 0 && (
                                <div className="flex justify-between border-b border-card-border pb-1">
                                  <span className="text-text-secondary">
                                    {t('importHistory.detail.fields.discount', 'Descuento')}
                                  </span>
                                  <span className="font-medium text-error">
                                    -{item.order_data_snapshot?.total_discount} {item.order_data_snapshot?.currency}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Customer & Shipping Info (Full width below) */}
                        {hasShipping && (
                          <div className="bg-body-bg/20 border border-card-border rounded-xl p-4 flex flex-col gap-4 md:col-span-2">
                            <h4 className="text-xs font-bold text-text-secondary uppercase tracking-widest flex items-center gap-2 mb-2">
                              <FontAwesomeIcon icon={faTruck} />
                              {t('importHistory.detail.sections.shipping', 'Datos de Envío')}
                            </h4>

                            <div className="space-y-3">
                              {(item.order_data_snapshot?.shipping_name ||
                                item.order_data_snapshot?.email ||
                                item.order_data_snapshot?.phone) && (
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-text-secondary uppercase font-bold">
                                    {t('importHistory.detail.fields.customer', 'Cliente')}
                                  </span>
                                  {item.order_data_snapshot?.shipping_name && (
                                    <span className="text-sm font-medium">
                                      {item.order_data_snapshot.shipping_name}
                                    </span>
                                  )}
                                  {item.order_data_snapshot?.email && (
                                    <span className="text-xs text-text-secondary">
                                      {item.order_data_snapshot.email}
                                    </span>
                                  )}
                                  {item.order_data_snapshot?.phone && (
                                    <span className="text-xs text-text-secondary">
                                      {item.order_data_snapshot.phone}
                                    </span>
                                  )}
                                </div>
                              )}

                              {item.order_data_snapshot?.shipping_address1 && (
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-text-secondary uppercase font-bold">
                                    {t('importHistory.detail.fields.address', 'Dirección')}
                                  </span>
                                  <span className="text-sm font-medium whitespace-pre-line">
                                    {item.order_data_snapshot.shipping_address1}
                                    {item.order_data_snapshot.shipping_address2 &&
                                      `, ${item.order_data_snapshot.shipping_address2}`}
                                    {'\n'}
                                    {item.order_data_snapshot.shipping_zip} {item.order_data_snapshot.shipping_city}
                                    {item.order_data_snapshot.shipping_province &&
                                      `, ${item.order_data_snapshot.shipping_province}`}
                                    {'\n'}
                                    {item.order_data_snapshot.shipping_country_code}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="filters" className="mt-0">
                <div className="flex flex-col gap-4">
                  {item.applied_filters_result ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Inclusiones */}
                      <div className="flex flex-col gap-3">
                        <h4 className="text-xs font-bold text-text-secondary uppercase tracking-widest flex items-center gap-2">
                          {t('importHistory.detail.inclusions', 'Filtros de Inclusión')}
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(item.filters_applied?.include || {}).map(([key, config]) => {
                            const passed = item.applied_filters_result?.includeResults?.[key];
                            return (
                              <div
                                key={key}
                                className={cn(
                                  'flex items-center justify-between p-3 rounded-lg border',
                                  passed ? 'bg-success/5 border-success/20' : 'bg-body-bg/40 border-card-border',
                                )}
                              >
                                <div className="flex flex-col">
                                  <span className={cn('text-xs font-medium capitalize', !passed && 'text-error')}>
                                    {key.replace('_', ' ')}
                                  </span>
                                  <span className={cn('text-[10px]', passed ? 'text-text-secondary' : 'text-error/70')}>
                                    Req: {config.join(', ')}
                                  </span>
                                </div>
                                <FontAwesomeIcon
                                  icon={passed ? faCircleCheck : faCircleXmark}
                                  className={passed ? 'text-title' : 'text-error'}
                                />
                              </div>
                            );
                          })}
                          {(!item.filters_applied?.include ||
                            Object.keys(item.filters_applied.include).length === 0) && (
                            <p className="text-xs text-text-secondary italic">
                              {t('importHistory.detail.noFilters', 'Sin filtros configurados')}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Exclusiones */}
                      <div className="flex flex-col gap-3">
                        <h4 className="text-xs font-bold text-text-secondary uppercase tracking-widest flex items-center gap-2">
                          {t('importHistory.detail.exclusions', 'Filtros de Exclusión')}
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(item.filters_applied?.exclude || {}).map(([key, config]) => {
                            const excluded = item.applied_filters_result?.excludeResults?.[key];
                            // exclude passed means it was VALID for inclusion (not excluded)
                            // wait, if excludeResults[key] is true, it means it MATCHED the exclusion rule, so it's bad.
                            return (
                              <div
                                key={key}
                                className={cn(
                                  'flex items-center justify-between p-3 rounded-lg border',
                                  excluded ? 'bg-error/5 border-error/20' : 'bg-body-bg/40 border-card-border',
                                )}
                              >
                                <div className="flex flex-col">
                                  <span className={cn('text-xs font-medium capitalize', excluded && 'text-error')}>
                                    {key.replace('_', ' ')}
                                  </span>
                                  <span
                                    className={cn('text-[10px]', excluded ? 'text-error/70' : 'text-text-secondary')}
                                  >
                                    Ban: {config.join(', ')}
                                  </span>
                                </div>
                                <FontAwesomeIcon
                                  icon={excluded ? faCircleXmark : faCircleCheck}
                                  className={excluded ? 'text-error' : 'text-title'}
                                />
                              </div>
                            );
                          })}
                          {(!item.filters_applied?.exclude ||
                            Object.keys(item.filters_applied.exclude).length === 0) && (
                            <p className="text-xs text-text-secondary italic">
                              {t('importHistory.detail.noFilters', 'Sin filtros configurados')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-body-bg/20 rounded-xl border border-dashed border-card-border">
                      <p className="text-sm text-text-secondary italic">
                        {t('importHistory.detail.noFiltersRun', 'No se ejecutaron filtros para este registro')}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <Badge variant="outline" className="animate-pulse">
              {t('common.loading', 'Cargando...')}
            </Badge>
          </div>
        )}
      </ModalCenter>
    );
  },
);

ImportHistoryDetailModal.displayName = 'ImportHistoryDetailModal';
